import { useState, useEffect, useMemo, useRef } from 'react';
import type { StepContentProps } from '~/ui-rocco/multi-step-form';
import { Input } from '@rocco/components/input';
import { StepContent } from '../StepContent';
import { HDE_ATTENDANCE_REASONS } from '../registration-options';
import type { GroupedSelectOptionType } from '../../../utils/form-options';
import type { LocationOption } from '../types';
import { HdeOptionList } from '../HDEOptionList';
import {
    getHdeFieldLabelClassName,
    getHdeInputClassName,
} from '../hde-option-styles';

interface EventAttendanceData {
    eventId: string | null;
    attendanceReasons: GroupedSelectOptionType[];
    otherReason: string;
    locationOptions?: LocationOption[];
}

export function EventAttendanceStep({
    setIsValid,
    onDataChange,
    initialData,
}: StepContentProps) {
    const data = initialData as EventAttendanceData | undefined;
    const locationOptions = useMemo(
        () => data?.locationOptions || [],
        [data?.locationOptions],
    );
    const [eventId, setEventId] = useState<string | null>(
        data?.eventId ||
            (locationOptions.length > 0
                ? locationOptions[0]?.value || null
                : null),
    );
    const [attendanceReasons, setAttendanceReasons] = useState<
        GroupedSelectOptionType[]
    >(data?.attendanceReasons || []);
    const [otherReason, setOtherReason] = useState(data?.otherReason || '');
    const otherReasonInputRef = useRef<HTMLInputElement>(null);

    const needOtherReason = attendanceReasons.some((r) => r.value === 'Other');

    useEffect(() => {
        if (needOtherReason) {
            otherReasonInputRef.current?.focus();
        }
    }, [needOtherReason]);

    useEffect(() => {
        if (locationOptions.length > 0 && !eventId) {
            setEventId(locationOptions[0]?.value || null);
        }
    }, [locationOptions, eventId]);

    useEffect(() => {
        const isValid =
            eventId !== null &&
            attendanceReasons.length > 0 &&
            (!needOtherReason || otherReason.trim().length > 0);

        setIsValid(isValid);

        onDataChange?.({
            eventId,
            attendanceReasons,
            otherReason: needOtherReason ? otherReason : '',
        });
    }, [
        eventId,
        attendanceReasons,
        otherReason,
        needOtherReason,
        setIsValid,
        onDataChange,
        locationOptions,
        data?.eventId,
    ]);

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <StepContent>
                <div className="space-y-3">
                    <HdeOptionList
                        label="What is your reason for attending? *"
                        mode="multi"
                        options={HDE_ATTENDANCE_REASONS}
                        value={attendanceReasons}
                        onChange={setAttendanceReasons}
                        contentAfterOption={
                            needOtherReason
                                ? {
                                      optionValue: 'Other',
                                      content: (
                                          <div className="space-y-3">
                                              <label
                                                  className={getHdeFieldLabelClassName()}
                                              >
                                                  Please specify *
                                              </label>
                                              <Input
                                                  ref={otherReasonInputRef}
                                                  value={otherReason}
                                                  onChange={(e) =>
                                                      setOtherReason(
                                                          e.target.value,
                                                      )
                                                  }
                                                  placeholder="Please specify"
                                                  className={getHdeInputClassName()}
                                              />
                                          </div>
                                      ),
                                  }
                                : undefined
                        }
                    />
                </div>
            </StepContent>
        </div>
    );
}
